import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Avatar } from '@/components/Avatar';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { getApiErrorMessage } from '@/lib/apiError';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/** Résultats proposés par recherche (liste paginée côté API : on affine la recherche plutôt que de tout charger) */
export const SEARCH_RESULTS = 10;

/** Personne proposée : utilisateur, employé… (NOM Prénom, email) */
export interface Person {
  id: id;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
}

/** Résultat d'une recherche : une page de personnes, le total, et pourquoi certaines ne sont pas proposables */
export interface PersonSearchResult<T extends Person> {
  items: T[];
  totalItems: number;
  /** Raison affichée après le nom des personnes grisées, par id (ex. « déjà employé ») */
  disabledReasons: Map<string, string>;
}

interface Candidates<T extends Person> extends PersonSearchResult<T> {
  query: string;
  error: string | null;
}

/** Option de la liste : une personne, grisée avec sa raison */
type Option<T> = { person: T; disabledReason: string | undefined };

/**
 * Choix d'une personne dans une liste paginée côté API (combobox ARIA) : rien n'est proposé avant la saisie, la recherche
 * part à l'API (search(), 10 résultats), les personnes non proposables sont grisées avec leur raison ; un bouton + à droite
 * du champ en crée une (seulement si onCreate est fourni).
 * Clavier : flèches pour se déplacer, Entrée pour choisir, Échap pour fermer. Une fois choisie, la personne
 * s'affiche dans le champ (pastille), la croix permet d'en changer.
 * Utilisée par UserCombobox (utilisateurs à ajouter à une société) et MemberCombobox (employés à ajouter à une équipe).
 */
export function PersonCombobox<T extends Person>({
  id,
  label,
  value,
  onChange,
  search,
  searchKey,
  onCreate,
  createLabel,
  noResultCreateText,
  noResultText,
}: {
  id: string;
  /** Nom de la liste pour les lecteurs d'écran (« Utilisateurs ») */
  label: string;
  value: T | null;
  onChange: (person: T | null) => void;
  search: (query: string) => Promise<PersonSearchResult<T>>;
  /** Contexte de la recherche (société, équipe) : le changer relance la recherche */
  searchKey: string;
  /** Bouton + : création d'une personne ; absent, pas de bouton */
  onCreate?: () => void;
  createLabel?: string;
  /** Aucun résultat, avec bouton + : texte suivi de l'icône + (« Aucun utilisateur ne correspond : créez-le avec ») */
  noResultCreateText?: string;
  /** Aucun résultat (sans bouton +) */
  noResultText: string;
}) {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [candidates, setCandidates] = useState<Candidates<T> | null>(null);
  // search() est recréée à chaque rendu du parent : la recherche ne dépend que de searchKey
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  });
  const query = useDebouncedValue(text).trim();

  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    searchRef
      .current(query)
      .then((result) => !cancelled && setCandidates({ ...result, query, error: null }))
      .catch(
        (e) =>
          !cancelled &&
          setCandidates({ query, items: [], totalItems: 0, disabledReasons: new Map(), error: getApiErrorMessage(e) }),
      );

    return () => {
      cancelled = true;
    };
  }, [searchKey, query]);

  // Résultats de la saisie courante seulement ; rien tant que rien n'est saisi
  const results = text.trim() && candidates ? candidates : null;
  const searching = !!text.trim() && candidates?.query !== text.trim();
  const options: Option<T>[] = (results?.items ?? []).map((person) => ({
    person,
    disabledReason: results!.disabledReasons.get(String(person.id)),
  }));
  const active = Math.min(activeIndex, options.length - 1);

  const choose = (option: Option<T> | undefined) => {
    if (!option || option.disabledReason) return;
    setOpen(false);
    onChange(option.person);
    setText('');
  };

  const move = (step: 1 | -1) => {
    if (!options.length) return;
    let next = active;
    for (let i = 0; i < options.length; i++) {
      next = (next + step + options.length) % options.length;
      if (!options[next].disabledReason) break;
    }
    setActiveIndex(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      choose(options[active]);
    } else if (e.key === 'Escape' && open) {
      // Ferme la liste sans fermer la fenêtre
      e.stopPropagation();
      setOpen(false);
    }
  };

  if (value) {
    return (
      <div className="w-full flex items-center gap-3 px-3 py-2 border border-slate-300 rounded-lg bg-card">
        <Avatar firstName={value.firstName} lastName={value.lastName} size="sm" tone="accent" />
        <span className="flex-1 min-w-0 text-sm" data-testid="selected-person">
          <span className="block font-medium text-slate-900 truncate">{value.fullName}</span>
          <span className="block text-xs text-slate-500 truncate">{value.email}</span>
        </span>
        <button
          type="button"
          aria-label="Changer de personne"
          onClick={() => {
            onChange(null);
            setTimeout(() => inputRef.current?.focus());
          }}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const showList = open && !!text.trim();

  return (
    <div className="relative">
      {/* Loupe centrée sur le champ seul (pas sur le texte d'aide en dessous) */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
        <input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={showList && options[active] ? `${listboxId}-${active}` : undefined}
          autoComplete="off"
          placeholder="Rechercher par nom, prénom ou email..."
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          className={clsx(
            'w-full pl-10 py-2 border border-slate-300 rounded-lg text-sm bg-card focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none',
            onCreate ? 'pr-11' : 'pr-4',
          )}
        />
        {/* Création d'une personne absente de la recherche */}
        {onCreate && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={createLabel}
                onClick={onCreate}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{createLabel}</TooltipContent>
          </Tooltip>
        )}
      </div>
      {!text.trim() && <p className="mt-2 text-xs text-slate-500">Commencez à saisir un nom, un prénom ou un email.</p>}

      {showList && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={label}
          // Clic sur une option : le champ ne doit pas perdre le focus (fermeture) avant la sélection
          onMouseDown={(e) => e.preventDefault()}
          className={clsx(
            'absolute z-20 mt-1 w-full bg-card border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto py-1',
            // Recherche en cours : résultats précédents estompés, le fond reste opaque
            searching && '[&>li]:opacity-60',
          )}
        >
          {searching && !results?.items.length && <li className="px-3 py-2 text-sm text-slate-500">Recherche…</li>}
          {results?.error && (
            <li role="alert" className="px-3 py-2 text-sm text-destructive">
              {results.error}
            </li>
          )}
          {results && !searching && !results.items.length && !results.error && (
            <li className="px-3 py-2 text-sm text-slate-500">
              {onCreate ? (
                <>
                  {noResultCreateText ?? 'Personne ne correspond : créez-la avec'}{' '}
                  <Plus className="inline w-3.5 h-3.5 align-text-bottom" aria-label="+" />.
                </>
              ) : (
                noResultText
              )}
            </li>
          )}
          {options.map((option, index) => (
            <li
              key={option.person.id}
              id={`${listboxId}-${index}`}
              role="option"
              aria-selected={index === active}
              aria-disabled={!!option.disabledReason}
              onMouseEnter={() => !option.disabledReason && setActiveIndex(index)}
              onClick={() => choose(option)}
              className={clsx(
                'px-3 py-2 flex items-center gap-3 text-sm',
                option.disabledReason ? 'text-slate-400 cursor-not-allowed' : 'cursor-pointer',
                index === active && !option.disabledReason && 'bg-blue-50',
              )}
            >
              <Avatar firstName={option.person.firstName} lastName={option.person.lastName} size="sm" />
              <span className="flex-1 min-w-0">
                <span className={clsx('block font-medium truncate', !option.disabledReason && 'text-slate-900')}>
                  {option.person.fullName}
                  {option.disabledReason && ` (${option.disabledReason})`}
                </span>
                <span className="block text-xs truncate">{option.person.email}</span>
              </span>
            </li>
          ))}
          {results && results.totalItems > results.items.length && (
            <li className="px-3 py-2 text-xs text-slate-500 border-t border-slate-100">
              {results.items.length} premiers résultats sur {results.totalItems} : précisez la recherche.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
